import { decode } from "html-entities";
import { store } from "../redux/stores/store";
import * as types from "../redux/types/job_type";

const clearAndFetchJobs = (updateJobId, clearJobList, getJobs, startCursor) => {
  if (store.getState().jobs.isLoadingJobs) return;

  store.getState().jobs.isLoadingJobs = true;

  //Cancel any existing call to graphQL
  if (store.getState().axios.cancelTokens[types.FETCHING_JOBS_KEY]) {
    store
      .getState()
      .axios.cancelTokens[types.FETCHING_JOBS_KEY].cancel("job search navbar");
  }

  updateJobId("");

  clearJobList().then((clearResponse) => {
    let params = formJobQueryParam(store.getState().jobs.jobListFilter, true);

    const pagination = {
      first: null,
      last: null,
      startCursor: null,
      endCursor: null,
    };

    getJobs(params, pagination)
      .then((response) => {
        store.getState().jobs.isLoadingJobs = false;
        store.getState().jobs.jobCurrentPage = 1;
      })
      .catch((error) => {
        store.getState().jobs.isLoadingJobs = false;
        store.getState().jobs.jobCurrentPage = 1;
      });
  });
};

const formJobQueryParam = (jobFilter, refresh) => {
  let currentLoadingJobsKey = new Date().getTime();
  store.getState().jobs.currentLoadingJobsKey = currentLoadingJobsKey;

  let params = {
    refresh: refresh,
    page: refresh ? 1 : store.getState().jobs.jobCurrentPage,
    canCancel: false,
    currentLoadingJobsKey: currentLoadingJobsKey,
    cancelTokenKey: types.FETCHING_JOBS_KEY,
    salary: jobFilter["salary"] ? jobFilter["salary"] : 0,
  };

  if (jobFilter["keyword"] && jobFilter["keyword"] != "") {
    params["keyword"] = jobFilter["keyword"];
  } else {
    params["keyword"] = "*";
  }

  if (jobFilter["specialisation"]?.length > 0) {
    params["specialisations"] = jobFilter["specialisation"];
  }

  if (jobFilter["state"]?.length > 0) {
    params["state"] = jobFilter["state"];
  }

  if (jobFilter["jobType"]?.length > 0) {
    params["jobType"] = jobFilter["jobType"];
  }

  if (jobFilter["experience"]?.length > 0) {
    params["experience"] = jobFilter["experience"];
  }

  if (jobFilter["company_ids"]?.length > 0) {
    params["companyIds"] = jobFilter["company_ids"];
  }

  if (jobFilter["operator"] && jobFilter["operator"] != "") {
    params["operator"] = jobFilter["operator"];
  } else {
    params["operator"] = "and";
  }

  if (jobFilter["globalHire"] && jobFilter["globalHire"] != "") {
    params["globalHire"] = jobFilter["globalHire"];
  }

  return params;
};

const formJobSuggestionQueryParam = (jobFilter, refresh) => {
  let currentSuggestionKey = new Date().getTime();
  store.getState().jobs.currentSuggestionKey = currentSuggestionKey;

  let params = {
    refresh: refresh,
    page: refresh
      ? 1
      : store.getState().jobs.suggestionPage
      ? store.getState().jobs.suggestionPage
      : 1,
    canCancel: false,
    currentSuggestionKey: currentSuggestionKey,
    cancelTokenKey: types.FETCH_JOB_SUGGESTION_KEY,
    expectedSalary: jobFilter["expected_salary"]
      ? jobFilter["expected_salary"]
      : 0,
  };

  if (jobFilter["keyword"] && jobFilter["keyword"] != "") {
    params["keyword"] = jobFilter["keyword"];
  } else {
    params["keyword"] = "*";
  }

  if (jobFilter["track_ids"]?.length > 0) {
    params["trackIds"] = jobFilter["track_ids"];
  }

  if (jobFilter["state_region_names"]?.length > 0) {
    params["stateRegions"] = jobFilter["state_region_names"];
  }

  if (jobFilter["job_type_ids"]?.length > 0) {
    params["jobTypeIds"] = jobFilter["job_type_ids"];
  }

  if (jobFilter["experience_ids"]?.length > 0) {
    params["experienceIds"] = jobFilter["experience_ids"];
  }

  return params;
};

/**
 * Decodes hex/HTML entities from GPT summary.
 * Bullet-formatted summaries ("• line\n• line") become an array; paragraphs stay as a string.
 * @param {object} job
 * @param {{ preferBahasa?: boolean }} [options] — when true, uses `gptSummaryMs` if present, else `gptSummary`
 * @returns {string | string[]} — empty string when no summary
 */
function convertGptSummary(job, options = {}) {
  const preferBahasa = Boolean(options?.preferBahasa);

  const raw =
    preferBahasa && job?.gptSummaryMs ? job.gptSummaryMs : job?.gptSummary;
  if (!raw) return "";

  let regexHex = /&#x([0-9a-fA-F]+);/g;

  let result = raw.replace(regexHex, (_, hexValue) => {
    let char = String.fromCharCode(parseInt(hexValue, 16));
    return char;
  });

  const decoded = decode(result).trim();
  if (!decoded) return "";

  const lines = decoded
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const isBullets = lines.some((line) => /^[•\u2022]/.test(line));
  if (!isBullets) return decoded;

  return lines
    .map((line) => line.replace(/^[•\u2022]\s*/, "").trim())
    .filter(Boolean);
}

function truncateSkills(job) {
  if (!job?.skills?.length) return "";

  let skillsString = job.skills.map((skill) => skill.name).join(", ");
  if (skillsString.length <= 70) return { fullString: skillsString };

  let truncated = skillsString.slice(0, 70).trim();
  let lastCommaIndex = truncated.lastIndexOf(",");
  if (lastCommaIndex > 0) {
    truncated = truncated.slice(0, lastCommaIndex);
  }
  // Count displayed items by matching the truncated string with original skills
  const displayedSkills = job.skills.filter((skill) =>
    truncated.includes(skill.name)
  ).length;
  const remainingCount = job.skills.length - displayedSkills;

  return {
    fullString: skillsString,
    truncated: truncated + "...",
    remainingCount: remainingCount,
  };
}

function truncateBenefits(job) {
  if (!job?.company?.benefits?.length) return "";

  let benefitsString = job?.company?.benefits
    ?.map((benefit) => benefit?.title)
    .join(", ");
  if (benefitsString.length <= 70) return { fullString: benefitsString };

  let truncated = benefitsString.slice(0, 70).trim();
  let lastCommaIndex = truncated.lastIndexOf(",");
  if (lastCommaIndex > 0) {
    truncated = truncated.slice(0, lastCommaIndex);
  }
  // Count displayed items by matching the truncated string with original benefits
  const displayedBenefits = job.company.benefits.filter((benefit) =>
    truncated.includes(benefit.title)
  ).length;

  const remainingCount = job.company.benefits.length - displayedBenefits;

  return {
    fullString: benefitsString,
    truncated: truncated + "...",
    remainingCount: remainingCount,
  };
}

export {
  clearAndFetchJobs,
  convertGptSummary,
  formJobQueryParam,
  formJobSuggestionQueryParam,
  truncateBenefits,
  truncateSkills,
};
